import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  MessageCircle, Send, Loader2, Trophy, Dumbbell, Target, 
  Heart, Brain, Smile, Frown, AlertCircle, CheckCircle, 
  ChevronDown, ChevronUp, Calendar, Zap, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

export default function SessionJournal({ childId, childName }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessionType, setSessionType] = useState('training');
  const [scorecard, setScorecard] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [stats, setStats] = useState(null);
  const messagesEndRef = useRef(null);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/session-journal/history/${childId}?limit=10`);
      setHistory(res.data.sessions || []);
    } catch (err) {
      console.error('Failed to fetch history');
    }
  }, [childId]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/session-journal/stats/${childId}`);
      setStats(res.data.stats);
    } catch (err) {
      console.error('Failed to fetch stats');
    }
  }, [childId]);

  useEffect(() => {
    if (childId) {
      fetchHistory();
      fetchStats();
    }
  }, [childId, fetchHistory, fetchStats]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const startNewSession = () => {
    setMessages([{
      role: 'assistant',
      content: `Hey ${childName?.split(' ')[0] || 'there'}! 👋 Ready to log your ${sessionType}? Tell me about it - how did it go today?`
    }]);
    setSessionId(null);
    setScorecard(null);
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/session-journal/chat`, {
        child_id: childId,
        session_id: sessionId,
        message: userMessage,
        session_type: sessionType
      });

      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);

      if (res.data.scorecard_ready && res.data.scorecard) {
        setScorecard(res.data.scorecard);
        fetchHistory();
        fetchStats();
        toast.success('Session scorecard created! 🎉');
      }
    } catch (err) {
      toast.error('Failed to send message');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Sorry, I'm having trouble right now. Please try again." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const getRatingColor = (value, max = 10) => {
    const pct = (value / max) * 100;
    if (pct >= 70) return 'text-green-500';
    if (pct >= 50) return 'text-yellow-500';
    return 'text-[#ff025f]';
  };

  const getRatingBg = (value, max = 10) => {
    const pct = (value / max) * 100;
    if (pct >= 70) return 'bg-green-500';
    if (pct >= 50) return 'bg-yellow-500';
    return 'bg-[#ff025f]';
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="session-journal">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#02b5ff]/20 to-[#ff025f]/20 p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#02b5ff] to-[#ff025f] rounded-full flex items-center justify-center">
              <MessageCircle className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white">Session Journal</h3>
              <p className="text-xs text-zinc-400">Document your training & matches with AI</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={sessionType}
              onChange={(e) => setSessionType(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-sm"
            >
              <option value="training">Training</option>
              <option value="match">Match</option>
              <option value="competition">Competition</option>
            </select>
            <Button
              onClick={startNewSession}
              size="sm"
              className="bg-[#02b5ff] text-white rounded-full text-xs"
            >
              New Entry
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        {stats && (
          <div className="grid grid-cols-4 gap-2 mt-3">
            <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-white">{stats.total_sessions}</div>
              <div className="text-[10px] text-zinc-500">Sessions</div>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-[#02b5ff]">{stats.avg_performance || '-'}</div>
              <div className="text-[10px] text-zinc-500">Avg Perf</div>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-yellow-500">{stats.avg_rpe || '-'}</div>
              <div className="text-[10px] text-zinc-500">Avg RPE</div>
            </div>
            {stats.match_record && (
              <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                <div className="text-lg font-bold text-green-500">
                  {stats.match_record.wins}W-{stats.match_record.losses}L
                </div>
                <div className="text-[10px] text-zinc-500">Record</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Chat Area */}
      <div className="h-64 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <Brain className="h-10 w-10 text-zinc-600 mx-auto mb-2" />
            <p className="text-zinc-500 text-sm">Click "New Entry" to start documenting your session</p>
            <p className="text-zinc-600 text-xs mt-1">The AI will help capture your thoughts and create a scorecard</p>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                msg.role === 'user' 
                  ? 'bg-[#02b5ff] text-white rounded-br-sm' 
                  : 'bg-zinc-800 text-zinc-200 rounded-bl-sm'
              }`}>
                <p className="text-sm">{msg.content}</p>
              </div>
            </div>
          ))
        )}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-zinc-800 rounded-2xl px-4 py-2 rounded-bl-sm">
              <Loader2 className="h-4 w-4 animate-spin text-[#02b5ff]" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      {messages.length > 0 && !scorecard && (
        <div className="p-3 border-t border-zinc-800">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Tell me about your session..."
              className="flex-1 bg-zinc-800 border border-zinc-700 rounded-full px-4 py-2 text-white text-sm focus:border-[#02b5ff] outline-none"
              disabled={loading}
            />
            <Button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              className="bg-[#02b5ff] text-white rounded-full px-4"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Scorecard Display */}
      {scorecard && (
        <div className="p-4 border-t border-zinc-800 bg-gradient-to-b from-zinc-900 to-zinc-950">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="h-5 w-5 text-yellow-500" />
            <h4 className="font-bold text-white">Session Scorecard</h4>
            <span className="text-xs text-zinc-500 ml-auto">{scorecard.date}</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {scorecard.rpe && (
              <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                <Zap className="h-4 w-4 text-yellow-500 mx-auto mb-1" />
                <div className={`text-xl font-bold ${getRatingColor(scorecard.rpe)}`}>{scorecard.rpe}</div>
                <div className="text-[10px] text-zinc-500">Effort (RPE)</div>
              </div>
            )}
            {scorecard.performance_rating && (
              <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                <Target className="h-4 w-4 text-[#02b5ff] mx-auto mb-1" />
                <div className={`text-xl font-bold ${getRatingColor(scorecard.performance_rating)}`}>
                  {scorecard.performance_rating}
                </div>
                <div className="text-[10px] text-zinc-500">Performance</div>
              </div>
            )}
            {scorecard.mood_after && (
              <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                <Smile className="h-4 w-4 text-green-500 mx-auto mb-1" />
                <div className={`text-xl font-bold ${getRatingColor(scorecard.mood_after)}`}>
                  {scorecard.mood_after}
                </div>
                <div className="text-[10px] text-zinc-500">Mood After</div>
              </div>
            )}
            {scorecard.confidence_level && (
              <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                <TrendingUp className="h-4 w-4 text-[#ff025f] mx-auto mb-1" />
                <div className={`text-xl font-bold ${getRatingColor(scorecard.confidence_level)}`}>
                  {scorecard.confidence_level}
                </div>
                <div className="text-[10px] text-zinc-500">Confidence</div>
              </div>
            )}
          </div>

          {/* Match Result */}
          {scorecard.session_type === 'match' && scorecard.score && (
            <div className="bg-zinc-800/30 rounded-lg p-3 mb-3 text-center">
              <div className="text-2xl font-bold text-white">{scorecard.score}</div>
              {scorecard.result && (
                <span className={`text-sm px-3 py-1 rounded-full ${
                  scorecard.result === 'win' ? 'bg-green-500/20 text-green-400' :
                  scorecard.result === 'loss' ? 'bg-red-500/20 text-red-400' :
                  'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {scorecard.result.toUpperCase()}
                </span>
              )}
              {scorecard.opponent && (
                <p className="text-xs text-zinc-500 mt-1">vs {scorecard.opponent}</p>
              )}
            </div>
          )}

          {/* Highlights & Improvements */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            {scorecard.highlights?.length > 0 && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <div className="flex items-center gap-1 text-green-400 text-xs mb-2">
                  <CheckCircle className="h-3 w-3" /> Highlights
                </div>
                {scorecard.highlights.map((h, i) => (
                  <p key={i} className="text-xs text-zinc-300">• {h}</p>
                ))}
              </div>
            )}
            {scorecard.areas_to_improve?.length > 0 && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <div className="flex items-center gap-1 text-yellow-400 text-xs mb-2">
                  <Target className="h-3 w-3" /> To Improve
                </div>
                {scorecard.areas_to_improve.map((a, i) => (
                  <p key={i} className="text-xs text-zinc-300">• {a}</p>
                ))}
              </div>
            )}
          </div>

          {/* Worries */}
          {scorecard.worries?.length > 0 && (
            <div className="bg-[#ff025f]/10 border border-[#ff025f]/30 rounded-lg p-3 mb-3">
              <div className="flex items-center gap-1 text-[#ff025f] text-xs mb-2">
                <AlertCircle className="h-3 w-3" /> Concerns to Address
              </div>
              {scorecard.worries.map((w, i) => (
                <p key={i} className="text-xs text-zinc-300">• {w}</p>
              ))}
            </div>
          )}

          {/* Key Learnings & Next Focus */}
          {(scorecard.key_learnings || scorecard.next_focus) && (
            <div className="bg-zinc-800/30 rounded-lg p-3">
              {scorecard.key_learnings && (
                <div className="mb-2">
                  <span className="text-xs text-[#02b5ff]">💡 Key Learning:</span>
                  <p className="text-sm text-zinc-300">{scorecard.key_learnings}</p>
                </div>
              )}
              {scorecard.next_focus && (
                <div>
                  <span className="text-xs text-[#ff025f]">🎯 Next Focus:</span>
                  <p className="text-sm text-zinc-300">{scorecard.next_focus}</p>
                </div>
              )}
            </div>
          )}

          <Button
            onClick={startNewSession}
            className="w-full mt-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg"
          >
            Log Another Session
          </Button>
        </div>
      )}

      {/* History Toggle */}
      {history.length > 0 && (
        <div className="border-t border-zinc-800">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full p-3 flex items-center justify-between text-zinc-400 hover:bg-zinc-800/30"
          >
            <span className="text-sm flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Recent Sessions ({history.length})
            </span>
            {showHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          
          {showHistory && (
            <div className="p-3 pt-0 space-y-2 max-h-48 overflow-y-auto">
              {history.map((session, i) => (
                <div key={i} className="bg-zinc-800/30 rounded-lg p-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {session.scorecard?.session_type === 'match' ? (
                      <Trophy className="h-4 w-4 text-yellow-500" />
                    ) : (
                      <Dumbbell className="h-4 w-4 text-[#02b5ff]" />
                    )}
                    <div>
                      <span className="text-sm text-white capitalize">{session.scorecard?.session_type}</span>
                      <span className="text-xs text-zinc-500 ml-2">{session.scorecard?.date}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    {session.scorecard?.performance_rating && (
                      <span className={getRatingColor(session.scorecard.performance_rating)}>
                        Perf: {session.scorecard.performance_rating}
                      </span>
                    )}
                    {session.scorecard?.result && (
                      <span className={
                        session.scorecard.result === 'win' ? 'text-green-400' :
                        session.scorecard.result === 'loss' ? 'text-red-400' : 'text-yellow-400'
                      }>
                        {session.scorecard.result.toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
